import { useCallback, useState } from "react";
import { createChatLock, type ChatLockRecord } from "@veto/domain";
import { getMeta, setMeta } from "@veto/storage";

export function useChatLockSession() {
  const [pendingLock, setPendingLock] = useState<ChatLockRecord | null>(null);
  const [unlocked, setUnlocked] = useState<Set<string>>(() => new Set());

  const checkLock = useCallback(async (conversationId: string) => {
    if (unlocked.has(conversationId)) {
      setPendingLock(null);
      return true;
    }
    const raw = await getMeta(`chatlock.${conversationId}`);
    if (!raw) {
      setPendingLock(null);
      return true;
    }
    try {
      const lock = JSON.parse(raw) as ChatLockRecord;
      setPendingLock(lock);
      return false;
    } catch {
      setPendingLock(null);
      return true;
    }
  }, [unlocked]);

  const markUnlocked = useCallback((conversationId: string) => {
    setUnlocked((prev) => new Set(prev).add(conversationId));
    setPendingLock(null);
  }, []);

  const enableLock = useCallback(async (conversationId: string, pin: string) => {
    const lock = await createChatLock(conversationId, pin);
    await setMeta(`chatlock.${conversationId}`, JSON.stringify(lock));
    setUnlocked((prev) => new Set(prev).add(conversationId));
  }, []);

  const hasLock = useCallback(async (conversationId: string) => {
    const raw = await getMeta(`chatlock.${conversationId}`);
    return !!raw;
  }, []);

  /** Only after the chat was opened with its PIN in this session (else anyone at the screen could remove it). */
  const disableLock = useCallback(
    async (conversationId: string) => {
      if (!unlocked.has(conversationId)) return false;
      await setMeta(`chatlock.${conversationId}`, "");
      setUnlocked((prev) => {
        const n = new Set(prev);
        n.delete(conversationId);
        return n;
      });
      return true;
    },
    [unlocked],
  );

  return { pendingLock, checkLock, markUnlocked, enableLock, disableLock, hasLock, setPendingLock };
}
